import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { getHome } from 'state/actions/homeActions';
import { useDispatch, useAnalytics, useHome, useSession } from 'hooks';
import { useSeries } from 'hooks/useSeries';
import useProfile from 'hooks/users/useProfile';
import useShowPushNotification from 'hooks/notifications/useShowPushNotification';
import { routesPaths } from 'constants/routesPaths';
import { MIXPANEL_EVENTS } from 'constants/constants';

import FeaturedHero from 'components/featured/FeaturedHero/FeaturedHero';
import FeaturedCelebrities from 'components/celebrity/FeaturedCelebrities/FeaturedCelebrities';
import CategorySection from 'components/categoryList/CategorySection/CategorySection';
import KeepWatchingSection from 'components/series/KeepWatchingSection/KeepWatchingSection';
import Loading from 'components/common/Loading';

import { getFullName } from 'utils/helpers';
import parseError from 'utils/parseError';
import EventService from 'services/eventService';
import FeaturedUpcomingEvents from 'components/celebrity/FeaturedUpcomingEvents/FeaturedUpcomingEvents';
import './HomePage.scss';

const HomePage = () => {
  const { trackEvent } = useAnalytics();
  const { initializePushNotifications } = useShowPushNotification();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [liveEvents, setLiveEvents] = useState([]);

  useEffect(() => {
    initializePushNotifications();
  }, []);

  const getHomeRequest = useDispatch(getHome);
  const { keepWatchingList } = useSeries();
  const { authenticated } = useSession();

  const { celebrities, featuredCategories, content, loading } = useHome();
  const { requestProfile } = useProfile();

  useEffect(() => {
    if (!content?.length) {
      getHomeRequest();
    }
    if (authenticated) {
      requestProfile();
    }
    trackEvent(MIXPANEL_EVENTS.home);
  }, []);

  const getEvents = async () => {
    try {
      const { data: upcoming } = await EventService.getAllEvents('upcoming');
      const { data: live } = await EventService.getAllEvents('live');
      setUpcomingEvents(upcoming?.events || []);
      setLiveEvents(live?.events || []);
    } catch ({ response }) {
      parseError(response);
      setUpcomingEvents([]);
      setLiveEvents([]);
    }
  };

  const getLiveEvents = async () => {
    try {
      const { data: live } = await EventService.getAllEvents('live');
      setLiveEvents(live?.events || []);
    } catch ({ response }) {
      parseError(response);
      setLiveEvents([]);
    }
  };

  const getUpcomingEvents = async () => {
    try {
      const { data: upcoming } = await EventService.getAllEvents('upcoming');
      setUpcomingEvents(upcoming?.events || []);
    } catch ({ response }) {
      parseError(response);
      setUpcomingEvents([]);
    }
  };

  useEffect(() => {
    getEvents();

    const intervalId = setInterval(() => {
      getLiveEvents();
    }, 10000);

    return () => clearInterval(intervalId);
  }, []);

  const keepWatchingFiltered = keepWatchingList.filter(
    element => content.findIndex(({ id, liveEvent }) => id == element.id || !liveEvent) !== -1
  );

  const onCelebrityClick = celebrity => {
    const { firstName, lastName } = celebrity;
    trackEvent(MIXPANEL_EVENTS.featuredCreator, { creatorName: getFullName(firstName, lastName) });
  };

  const shouldShowPreviewLanding =
    !loading &&
    !content.length &&
    !celebrities.length &&
    !featuredCategories.length &&
    !liveEvents.length &&
    !upcomingEvents.length;

  if (loading) {
    return (
      <div className="home-loading">
        <Loading />
      </div>
    );
  }

  if (shouldShowPreviewLanding) {
    return (
      <div className="preview-homepage">
        <section className="preview-homepage__hero">
          <span className="preview-homepage__eyebrow">Identifi Internal Preview</span>
          <h1 className="preview-homepage__title">
            Review the platform experience from every operator view.
          </h1>
          <p className="preview-homepage__subtitle">
            This hosted preview is designed for team review. Choose a dashboard below — each has its
            own sidebar navigation so you can move between sections without returning here.
          </p>
        </section>

        <section className="preview-homepage__grid">
          <article className="preview-homepage__card preview-homepage__card--accent">
            <span className="preview-homepage__card-label">Creator dashboard</span>
            <h2 className="preview-homepage__card-title">
              Revenue, compliance, audience, and payout operations
            </h2>
            <p className="preview-homepage__card-copy">
              The role-gated creator workspace with sidebar navigation, enterprise finance cockpit,
              and content management.
            </p>
            <div className="preview-homepage__card-links">
              <Link className="preview-homepage__card-link" to={routesPaths.creatorDashboard}>
                Overview
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.creatorFinance}>
                Revenue & payouts
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.creatorContent}>
                Content
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.creatorAnalytics}>
                Fans & CRM
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.creatorEvents}>
                Events
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.creatorAds}>
                Ads
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.creatorSettings}>
                Settings
              </Link>
            </div>
          </article>

          <article className="preview-homepage__card preview-homepage__card--org">
            <span className="preview-homepage__card-label">Organization / league dashboard</span>
            <h2 className="preview-homepage__card-title">
              Season operations, sponsorships, approvals, and governance
            </h2>
            <p className="preview-homepage__card-copy">
              The multi-team operating layer with sponsor inventory, team management, and
              white-label controls.
            </p>
            <div className="preview-homepage__card-links">
              <Link className="preview-homepage__card-link" to={routesPaths.orgDashboard}>
                Overview
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.orgTeam}>
                Teams
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.orgEvents}>
                Events
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.orgFinance}>
                Finance
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.orgSettings}>
                Settings
              </Link>
            </div>
          </article>

          <article className="preview-homepage__card preview-homepage__card--admin">
            <span className="preview-homepage__card-label">Platform admin dashboard</span>
            <h2 className="preview-homepage__card-title">
              Platform oversight, risk review, and operator controls
            </h2>
            <p className="preview-homepage__card-copy">
              Separate admin path with platform metrics, creator Connect ops, approvals, support
              queues, and audit trails.
            </p>
            <div className="preview-homepage__card-links">
              <Link className="preview-homepage__card-link" to={routesPaths.adminLogin}>
                Admin login page
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.adminDashboard}>
                Dashboard
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.adminUsers}>
                Users
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.adminContent}>
                Content
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.adminFinance}>
                Finance
              </Link>
              <Link className="preview-homepage__card-link" to={routesPaths.adminSettings}>
                Settings
              </Link>
            </div>
          </article>
        </section>

        <section className="preview-homepage__grid preview-homepage__grid--legacy">
          <article className="preview-homepage__card preview-homepage__card--muted">
            <span className="preview-homepage__card-label">Legacy mock pages</span>
            <p className="preview-homepage__card-copy">
              The original standalone mock pages from before the architecture pass. Kept for
              reference.
            </p>
            <div className="preview-homepage__card-links">
              <Link className="preview-homepage__card-link" to={routesPaths.mockCreatorFinance}>
                Creator mock
              </Link>
              <Link
                className="preview-homepage__card-link"
                to={routesPaths.mockOrganizationDashboard}
              >
                Org mock
              </Link>
            </div>
          </article>
        </section>

        <section className="preview-homepage__notes">
          <div className="preview-homepage__note">
            <span className="preview-homepage__note-label">Navigation</span>
            <p>
              Each dashboard has its own sidebar — use it to move between sections. Click the
              Identifi logo or &ldquo;Back to feed&rdquo; in the top bar to return here.
            </p>
          </div>
          <div className="preview-homepage__note">
            <span className="preview-homepage__note-label">Auth in production</span>
            <p>
              In the live app, these dashboards are role-gated. Creators see /creator/*, orgs see
              /org/*, admins see /admin/*. The preview bypasses auth so the team can review all
              views.
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      {content.length > 0 && <FeaturedHero content={content} />}
      {keepWatchingFiltered && keepWatchingFiltered.length > 0 && (
        <KeepWatchingSection series={keepWatchingFiltered} />
      )}
      <FeaturedCelebrities
        celebrities={celebrities}
        titleId="featured.creators"
        onCelebrityClick={onCelebrityClick}
      />
      {liveEvents.length > 0 && <FeaturedUpcomingEvents title="Live Now" events={liveEvents} />}
      {upcomingEvents.length > 0 && (
        <FeaturedUpcomingEvents
          title="Upcoming Events"
          events={upcomingEvents}
          onSuccess={getUpcomingEvents}
        />
      )}

      {featuredCategories.map(category => (
        <CategorySection category={category} key={category.id} />
      ))}
    </>
  );
};

HomePage.loadData = async store => {
  await store.dispatch(getHome());
};

export default HomePage;
